// src/utils/formGridCells.js
//
// How wide a form element is DRAWN on the form layout grid (KPE-023). The
// layout says what an author asked for (`span`, or `auto`); the vendored
// limits table (utils/formGrid/limits, the same one the API and the layout
// editor read) says what the element's type can hold. This is the one place
// the renderer joins them, for the entry form and the read view alike, so the
// two faces of a record cannot draw one element two widths.
//
//   wide    columns of 12 — a form container 1024px or wider, as authored
//   medium  columns of 6  — a container from 640 to 1023px: the wide span
//                           halved and rounded up, and never below the
//                           minimum halved and rounded up (so a minimum that
//                           held at 12 columns still holds at 6)
//   narrow  one column    — under 640px every cell is the full row (CSS only)
//
// Pure: no React, no DOM.

import { COLUMNS, limitsFor, resolveSpan } from './formGrid/limits';

/** Columns in the medium tier. */
export const MEDIUM_COLUMNS = COLUMNS / 2;

// The container widths the tiers change at — the `@container form-grid`
// rules in css/components/form-grid.css, said once more for the code that has
// to NAME a tier (the layout editor's preview). A test reads the stylesheet
// and holds these to it, so the two cannot drift.
export const WIDE_MIN_WIDTH = 1024;
export const MEDIUM_MIN_WIDTH = 640;

/** The tiers, widest first: `{ key, columns, minWidth }` — `minWidth` the container width it starts at. */
export const GRID_TIERS = Object.freeze([
	Object.freeze({ key: 'wide', columns: COLUMNS, minWidth: WIDE_MIN_WIDTH }),
	Object.freeze({ key: 'medium', columns: MEDIUM_COLUMNS, minWidth: MEDIUM_MIN_WIDTH }),
	Object.freeze({ key: 'narrow', columns: 1, minWidth: 0 }),
]);

/** The tier a container `width` px wide is drawn in. */
export function gridTierFor(width) {
	const w = Number.isFinite(width) ? width : 0;
	return GRID_TIERS.find(tier => w >= tier.minWidth) || GRID_TIERS[GRID_TIERS.length - 1];
}

/**
 * The columns one element is drawn at.
 *
 *   cellSpans(element, requestedSpan)
 *     -> { wide, medium, min, auto, max }
 *
 * `requestedSpan` is what the layout entry carries (a whole number, 'auto', or
 * nothing). A number outside the type's limits is CLAMPED, never refused — the
 * layout save is where the API refuses, and a type changed after the span was
 * stored must still draw. Anything that is not a whole number reads as auto.
 *
 * The two halves of a date range are two elements with two entries, each held
 * to its own type (KPR-626; the API never knew a range, KPR-600): the form
 * draws them as two fields.
 */
export function cellSpans(element, requestedSpan = 'auto') {
	const limits = limitsFor(element);
	const wide = resolveSpan(element, requestedSpan);
	const medium = Math.min(MEDIUM_COLUMNS, Math.max(Math.ceil(wide / 2), Math.ceil(limits.min / 2)));
	return { wide, medium, min: limits.min, auto: limits.auto, max: limits.max };
}

/**
 * The one cell the READ VIEW draws a date range in (KPR-626, the owner's k07):
 * as wide as the form draws its two halves together, up to a full row, so a
 * row arranged on the form keeps its shape in the view. `start` and `end` are
 * each `cellSpans` of one half.
 */
export function mergedCellSpans(start, end) {
	const wide = Math.min(COLUMNS, start.wide + end.wide);
	const medium = Math.min(MEDIUM_COLUMNS, start.medium + end.medium);
	return { wide, medium };
}
